import { useRef, useState, useEffect } from 'react';
import {
  formatNumber,
  getTemperatureUsageForFlashingResin,
  getTemperatureUsageForProColorFinish,
  getTemperatureUsageForProPrimer,
  getTemperatureUsageForResinAggregate,
  roundUpToInteger,
  roundUpToOneDecimal
} from '../../../helpers/calculator';

function useMaterialEstimatingCalculator(props) {
  // get the labels from props
  const { current: labels } = useRef(JSON.parse(props.labels));
  // get the resin-types from props
  const { current: resinTypes } = useRef(JSON.parse(props.resinTypes));
  // get the product names from props
  const { current: productNames } = useRef(JSON.parse(props.productNames));
  //get the coverage managed labels
  const { current: productCoverageLabels } = useRef(JSON.parse(props.productCoverageLabels));

  // ---------------------------------------------------------
  // Input states
  // ---------------------------------------------------------
  //states for the resin type
  const [resinType, setResinType] = useState('');
  // states for the temperature
  const [temperature, setTemperature] = useState('');
  const [formattedTemperature, setFormattedTemperature] = useState(''); // to show in the input
  const [temperatureError, setTemperatureError] = useState(false);
  // states for the area
  const [area, setArea] = useState('');
  const [formattedArea, setFormattedArea] = useState('');
  const [areaError, setAreaError] = useState(false);
  // states for the flashing
  const [flashing, setFlashing] = useState(null);
  const [formattedFlashing, setFormattedFlashing] = useState('');
  const [flashingError, setFlashingError] = useState(false);
  // states for the waste
  const [waste, setWaste] = useState('10');
  const [formattedWaste, setFormattedWaste] = useState('10.00%');
  const [wasteError, setWasteError] = useState(false);
  // states for the quoted price extra options
  const [includePrice, setIncludePrice] = useState('');
  // states for the color pro finish extra option
  const [includeProColorFinish, setIncludeProColorFinish] = useState('');
  // states for the quoted price pro primer
  const [quotedPriceProPrimer, setQuotedPriceProPrimer] = useState('');
  const [formattedQuotedPriceProPrimer, setFormattedQuotedPriceProPrimer] =
    useState('');
  // states for the quoted price resin
  const [quotedPriceResin, setQuotedPriceResin] = useState('');
  const [formattedQuotedPriceResin, setFormattedQuotedPriceResin] =
    useState('');
  // states for the quoted price catalyst liquid
  const [quotedPriceCatalystLiquid, setQuotedPriceCatalystLiquid] =
    useState('');
  const [
    formattedQuotedPriceCatalystLiquid,
    setFormattedQuotedPriceCatalystLiquid
  ] = useState('');
  // states for the quoted price catalyst powder
  const [quotedPriceCatalystPowder, setQuotedPriceCatalystPowder] =
    useState('');
  const [
    formattedQuotedPriceCatalystPowder,
    setFormattedQuotedPriceCatalystPowder
  ] = useState('');
  // states for the quoted price pro color finish
  const [quotedPriceProColorFinish, setQuotedPriceProColorFinish] =
    useState('');
  const [
    formattedQuotedPriceProColorFinish,
    setFormattedQuotedPriceProColorFinish
  ] = useState('');
  //total estimated material cost state
  const [totalEstimatedMaterialCost, setTotalEstimatedMaterialCost] =
    useState(0);
  //primer layer dropdown state
  const [
    flashingPrimerLayerDropdownValue,
    setFlashingPrimerLayerDropdownValue
  ] = useState('proPrimerW');
  const [flashingLayerDropdownValue, setFlashingLayerDropdownValue] =
    useState('pmmaFlashing');
  const [colorFinishLayerDropdownValue, setColorFinishLayerDropdownValue] =
    useState('proColorFinish');
  const [
    membranePrimerLayerDropdownValue,
    setMembranePrimerLayerDropdownValue
  ] = useState('proPrimerT');
  const [
    membranePrimerLayerDropdownValue250Grid,
    setMembranePrimerLayerDropdownValue250Grid
  ] = useState('proPrimerWGrid');
  const [surfacingLayerDropdownValue220, setSurfacingLayerDropdownValue220] =
    useState('terapro220ResinAggregate');
  const [surfacingLayerDropdownValue250, setSurfacingLayerDropdownValue250] =
    useState('terapro250ResinAggregate');
  const [
    surfacingLayerDropdownValue250Grid,
    setSurfacingLayerDropdownValue250Grid
  ] = useState('terapro250GridResinAggregate');
  const [totalDropdownValue, setTotalDropdownValue] = useState('proPrimerW');

  // results state
  const [results, setResults] = useState({
    proPrimerW: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    proPrimerCatalystLiquid: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    pmmaFlashing: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    pmmaFlashingCatalystLiquid: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    proFleeceReinforcement: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    proColorFinish: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    proColorFinishCatalystLiquid: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    proPrimerT: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    proPrimerCatalystLiquidT: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    terapro220ResinAggregate: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    terapro220ResinAggregateCatalystPowder: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    terapro250ResinAggregate: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    terapro250ResinAggregateCatalystPowder: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    proPrimerWGrid: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    proGridReinforcement: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    proPrimerWGridCatalystLiquid: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    terapro250GridResinAggregate: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    terapro250GridResinAggregateCatalystPowder: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    },
    proCatalystLiquidTotal: {
      unitQuantity: 0,
      unitQuantityRoundedUp: 0,
      unitQuantityRoundedUpInteger: 0,
      unitSize: '',
      unitLabel: '',
      typicalCoverage: '',
      quotedCost: 0,
      totalCost: 0
    }
  });

  // state for the calculated button
  const [disableButton, setDisableButton] = useState(true);

  // state for display results
  const [displayResults, setDisplayResults] = useState(false);

  const [optionsResinTypes, setOptionsResinTypes] = useState([]);

  const setResinTypesSelectOptions = () => {
    const options = Object.keys(resinTypes).map(key => ({
      value: key,
      label: resinTypes[key].name
    }));
    setOptionsResinTypes(options);
  };

  const regexValidation = value => {
    return value
      .replace(/[^0-9.]/g, '') // Remove non-numeric and non-dot characters
      .replace(/(\..*)\./g, '$1') // Allow only one dot
      .replace(/^(\d*\.\d{0,2})\d*$/, '$1') // Allow up to two decimal places
      .replace(/(\d+\.\d{0,2}).*/g, '$1'); //Ensure only two decimal places
  };

  // resin type
  const validateResinType = resinType => {
    return resinType !== '';
  };
  const handleChangeResinType = e => {
    setDisplayResults(false); // force to rehit the calculate button
    const val = e.target.value;
    setResinType(val);
  };

  // temperature
  const validateTemperature = temp => {
    const tempNum = Number(temp);
    return tempNum >= 32 && tempNum <= 95;
  };
  const handleChangeTemperature = e => {
    setDisplayResults(false);
    const val = e.target.value;
    const temperature = regexValidation(val);
    setTemperature(temperature);
    setFormattedTemperature(temperature);
    setTemperatureError(!validateTemperature(temperature));
  };
  const handleBlurTemperature = e => {
    //if temperature is empty, set it to ''
    if (!temperature) {
      setFormattedTemperature('');
      return;
    }
    const formattedTemperature = formatNumber(temperature, {
      suffix: '°F'
    });
    setFormattedTemperature(formattedTemperature);
    setDisplayResults(false);
  };

  // area
  const validateArea = area => {
    if (area > 0) return true;
    return false;
  };
  const handleChangeArea = e => {
    setDisplayResults(false);
    const val = e.target.value;
    const area = regexValidation(val);
    setArea(area);
    setFormattedArea(area);
    setAreaError(!validateArea(area));
  };
  const handleBlurArea = e => {
    //if area is empty, set it to ''
    if (!area) {
      setFormattedArea('');
      return;
    }
    const formattedArea = formatNumber(area, {
      suffix: ' sq/ft'
    });
    setFormattedArea(formattedArea);
  };

  // flashing
  const validateFlashing = flashing => {
    if (flashing >= 0) return true;
    return false;
  };
  const handleChangeFlashing = e => {
    setDisplayResults(false);
    const val = e.target.value;
    const flashing = regexValidation(val);
    setFlashing(flashing);
    setFormattedFlashing(flashing);
    setFlashingError(!validateFlashing(flashing));
  };
  const handleBlurFlashing = e => {
    //if flashing is empty, set it to ''
    if (!flashing) {
      setFormattedFlashing('');
      return;
    }
    const formattedFlashing = formatNumber(flashing, {
      suffix: ' sq/ft'
    });
    setFormattedFlashing(formattedFlashing);
  };

  // waste
  const validateWaste = waste => {
    const wasteNum = Number(waste);
    return wasteNum >= 0 && wasteNum <= 30;
  };
  const handleChangeWaste = e => {
    setDisplayResults(false);
    const val = e.target.value;
    //clear any non-numeric characters
    const waste = regexValidation(val);
    setWaste(waste);
    setFormattedWaste(waste);
    setWasteError(!validateWaste(waste));
  };

  const handleBlurWaste = e => {
    //if waste is empty, set it to 10
    if (!waste) {
      setFormattedWaste('10%');
      return;
    }
    const formattedWaste = formatNumber(waste, {
      suffix: '%'
    });
    setFormattedWaste(formattedWaste);
  };

  // prices checkbox
  const handleChangePriceYes = e => {
    setDisplayResults(false);
    const val = e.target.checked;

    if (val) {
      setIncludePrice(true);
    } else {
      setIncludePrice(false);
    }
  };
  const handleChangePriceNo = e => {
    setDisplayResults(false);
    const val = e.target.checked;

    if (val) {
      setIncludePrice(false);
    } else {
      setIncludePrice(true);
    }
  };

  // color pro finish checkbox
  const handleChangeProColorFinishYes = e => {
    setDisplayResults(false);
    const val = e.target.checked;

    if (val) {
      setIncludeProColorFinish(true);
    } else {
      setIncludeProColorFinish(false);
    }
  };

  const handleChangeProColorFinishNo = e => {
    setDisplayResults(false);
    const val = e.target.checked;

    if (val) {
      setIncludeProColorFinish(false);
    } else {
      setIncludeProColorFinish(true);
    }
  };

  // quoted price pro primer
  const handleChangeQuotedPriceProPrimer = e => {
    setDisplayResults(false);
    const val = e.target.value;
    const quotedPriceProPrimer = regexValidation(val);
    setQuotedPriceProPrimer(quotedPriceProPrimer);
    setFormattedQuotedPriceProPrimer(quotedPriceProPrimer);
  };
  const handleBlurQuotedPriceProPrimer = e => {
    //if quotedPriceProPrimer is empty, set it to ''
    if (!quotedPriceProPrimer) {
      setFormattedQuotedPriceProPrimer('');
      return;
    }
    const formattedQuotedPriceProPrimer = formatNumber(quotedPriceProPrimer, {
      prefix: '$',
      suffix: ' / Pail'
    });
    setFormattedQuotedPriceProPrimer(formattedQuotedPriceProPrimer);
  };

  // quoted price resin
  const handleChangeQuotedPriceResin = e => {
    setDisplayResults(false);
    const val = e.target.value;
    const quotedPriceResin = regexValidation(val);
    setQuotedPriceResin(quotedPriceResin);
    setFormattedQuotedPriceResin(quotedPriceResin);
  };
  const handleBlurQuotedPriceResin = e => {
    //if quotedPriceResin is empty, set it to ''
    if (!quotedPriceResin) {
      setFormattedQuotedPriceResin('');
      return;
    }
    const formattedQuotedPriceResin = formatNumber(quotedPriceResin, {
      prefix: '$',
      suffix: ' / Pail'
    });
    setFormattedQuotedPriceResin(formattedQuotedPriceResin);
  };

  // quoted price catalyst liquid
  const handleChangeQuotedPriceCatalystLiquid = e => {
    setDisplayResults(false);
    const val = e.target.value;
    const quotedPriceCatalystLiquid = regexValidation(val);
    setQuotedPriceCatalystLiquid(quotedPriceCatalystLiquid);
    setFormattedQuotedPriceCatalystLiquid(quotedPriceCatalystLiquid);
  };
  const handleBlurQuotedPriceCatalystLiquid = e => {
    //if quotedPriceCatalystLiquid is empty, set it to ''
    if (!quotedPriceCatalystLiquid) {
      setFormattedQuotedPriceCatalystLiquid('');
      return;
    }
    const formattedQuotedPriceCatalystLiquid = formatNumber(
      quotedPriceCatalystLiquid,
      {
        prefix: '$',
        suffix: ' / Container'
      }
    );
    setFormattedQuotedPriceCatalystLiquid(formattedQuotedPriceCatalystLiquid);
  };

  // quoted price catalyst powder
  const handleChangeQuotedPriceCatalystPowder = e => {
    setDisplayResults(false);
    const val = e.target.value;
    const quotedPriceCatalystPowder = regexValidation(val);
    setQuotedPriceCatalystPowder(quotedPriceCatalystPowder);
    setFormattedQuotedPriceCatalystPowder(quotedPriceCatalystPowder);
  };
  const handleBlurQuotedPriceCatalystPowder = e => {
    //if quotedPriceCatalystPowder is empty, set it to ''
    if (!quotedPriceCatalystPowder) {
      setFormattedQuotedPriceCatalystPowder('');
      return;
    }
    const formattedQuotedPriceCatalystPowder = formatNumber(
      quotedPriceCatalystPowder,
      {
        prefix: '$',
        suffix: ' / Box'
      }
    );
    setFormattedQuotedPriceCatalystPowder(formattedQuotedPriceCatalystPowder);
  };

  // quoted price pro color finish
  const handleChangeQuotedPriceProColorFinish = e => {
    setDisplayResults(false);
    const val = e.target.value;
    const quotedPriceProColorFinish = regexValidation(val);
    setQuotedPriceProColorFinish(quotedPriceProColorFinish);
    setFormattedQuotedPriceProColorFinish(quotedPriceProColorFinish);
  };
  const handleBlurQuotedPriceProColorFinish = e => {
    //if quotedPriceProColorFinish is empty, set it to ''
    if (!quotedPriceProColorFinish) {
      setFormattedQuotedPriceProColorFinish('');
      return;
    }
    const formattedQuotedPriceProColorFinish = formatNumber(
      quotedPriceProColorFinish,
      {
        prefix: '$',
        suffix: ' / Pail'
      }
    );
    setFormattedQuotedPriceProColorFinish(formattedQuotedPriceProColorFinish);
  };

  const validateForm = () => {
    //validate temperature, area, flashing, waste
    if (!temperature || !area || !waste || !flashing) {
      return false;
    }
    //validate resin type
    if (!validateResinType(resinType)) {
      return false;
    }
    //validate temperature
    if (!validateTemperature(temperature)) {
      return false;
    }
    //validate area
    if (!validateArea(area)) {
      return false;
    }
    // validate flashing
    if (!validateFlashing(flashing)) {
      return false;
    }
    //validate waste
    if (!validateWaste(waste)) {
      return false;
    }
    //validate prices
    if (includePrice) {
      if (
        !quotedPriceProPrimer ||
        !quotedPriceResin ||
        !quotedPriceCatalystLiquid ||
        !quotedPriceCatalystPowder
      ) {
        return false;
      }
    } else {
      if (includePrice === null) {
        return false;
      }
    }
    //validate pro color finish
    if (includeProColorFinish === '') {
      return false;
    } else if (includeProColorFinish && includePrice) {
      if (!quotedPriceProColorFinish) {
        return false;
      }
    }
    return true;
  };

  const handleClickCalculate = e => {
    e.preventDefault();
    validateForm();
    if (validateForm()) {
      calculateResults();
      setDisplayResults(true);
    }
  };

  const calculateResults = () => {
    // Flashing system ---------------------------------------------------------
    // Pro Primer W
    let _totalEstimatedMaterialCost = 0,
      unitQuantity = 0,
      unitQuantityRoundedUp = 0,
      unitQuantityRoundedUpInteger = 0,
      quotedCost = 0,
      totalCost = 0,
      _proCatalystLiquidTotalUnitQuantity = 0;

    unitQuantity = flashing / 270 + (waste / 100) * (flashing / 270);
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceProPrimer) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _proPrimerW = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '10 kg pails',
      unitLabel: 'pail',
      typicalCoverage: '270',
      quotedCost,
      totalCost
    };

    _totalEstimatedMaterialCost += totalCost;

    setResults(prevResults => ({ ...prevResults, proPrimerW: _proPrimerW }));

    // Pro Catalyst Liquid for Pro Primer
    unitQuantity =
      (_proPrimerW.unitQuantity *
        getTemperatureUsageForProPrimer(temperature)) /
      10;
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceCatalystLiquid) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _proPrimerCatalystLiquid = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '2.5 kg container',
      unitLabel: 'container',
      typicalCoverage: '10',
      quotedCost,
      totalCost
    };

    _proCatalystLiquidTotalUnitQuantity += unitQuantity;

    setResults(prevResults => ({
      ...prevResults,
      proPrimerCatalystLiquid: _proPrimerCatalystLiquid
    }));

    // PMMA Flashing
    unitQuantity = flashing / 32 + (waste / 100) * (flashing / 32);
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceResin) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _pmmaFlashing = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '10 kg pails',
      unitLabel: 'pail',
      typicalCoverage: '32',
      quotedCost,
      totalCost
    };

    _totalEstimatedMaterialCost += totalCost;

    setResults(prevResults => ({
      ...prevResults,
      pmmaFlashing: _pmmaFlashing
    }));

    // Pro Catalyst Liquid for PMMA Flashing
    unitQuantity =
      (_pmmaFlashing.unitQuantity *
        getTemperatureUsageForFlashingResin(temperature)) /
      10;
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceCatalystLiquid) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _pmmaFlashingCatalystLiquid = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '2.5 kg container',
      unitLabel: 'container',
      typicalCoverage: '10',
      quotedCost,
      totalCost
    };

    _proCatalystLiquidTotalUnitQuantity += unitQuantity;

    setResults(prevResults => ({
      ...prevResults,
      pmmaFlashingCatalystLiquid: _pmmaFlashingCatalystLiquid
    }));

    // Pro Fleece reinforcement
    unitQuantity = flashing / 164 + (waste / 100) * (flashing / 164);
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceResin) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _proFleeceReinforcement = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '1 roll',
      unitLabel: 'roll',
      typicalCoverage: '164',
      quotedCost,
      totalCost
    };

    _totalEstimatedMaterialCost += totalCost;

    setResults(prevResults => ({
      ...prevResults,
      proFleeceReinforcement: _proFleeceReinforcement
    }));

    // Pro Color Finish
    unitQuantity = flashing / 215 + (waste / 100) * (flashing / 215);
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includeProColorFinish ? Number(quotedPriceProColorFinish) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _proColorFinish = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '10 kg pails',
      unitLabel: 'pail',
      typicalCoverage: '215',
      quotedCost,
      totalCost
    };

    if (includeProColorFinish) {
      _totalEstimatedMaterialCost += totalCost;
    }

    setResults(prevResults => ({
      ...prevResults,
      proColorFinish: _proColorFinish
    }));

    // Pro Catalyst Liquid for Pro Color Finish
    unitQuantity =
      (_proColorFinish.unitQuantity *
        getTemperatureUsageForProColorFinish(temperature)) /
      10;
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceCatalystLiquid) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _proColorFinishCatalystLiquid = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '2.5 kg container',
      unitLabel: 'container',
      typicalCoverage: '10',
      quotedCost,
      totalCost
    };

    if (includeProColorFinish) {
      _proCatalystLiquidTotalUnitQuantity += unitQuantity;
    }

    setResults(prevResults => ({
      ...prevResults,
      proColorFinishCatalystLiquid: _proColorFinishCatalystLiquid
    }));

    // Field Membrane ---------------------------------------------------------
    // Pro Primer T
    const proPrimerTConstant = resinType === 'terapro250grid' ? 135 : 270;
    unitQuantity =
      area / proPrimerTConstant + (waste / 100) * (area / proPrimerTConstant);
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceProPrimer) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _proPrimerT = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '10 kg pails',
      unitLabel: 'pail',
      typicalCoverage: '270',
      quotedCost,
      totalCost
    };
    _totalEstimatedMaterialCost += totalCost;
    setResults(prevResults => ({ ...prevResults, proPrimerT: _proPrimerT }));

    // Pro Catalyst Liquid for Pro Primer T
    unitQuantity =
      (_proPrimerT.unitQuantity *
        getTemperatureUsageForProPrimer(temperature)) /
      10;
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceCatalystLiquid) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _proPrimerCatalystLiquidT = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '2.5 kg container',
      unitLabel: 'container',
      typicalCoverage: '10',
      quotedCost,
      totalCost
    };

    _proCatalystLiquidTotalUnitQuantity += unitQuantity;

    setResults(prevResults => ({
      ...prevResults,
      proPrimerCatalystLiquidT: _proPrimerCatalystLiquidT
    }));

    // Terapro 220 resin aggregate
    unitQuantity = area / 136 + (waste / 100) * (area / 136);
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceResin) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _terapro220ResinAggregate = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '10 kg pails',
      unitLabel: 'pail',
      typicalCoverage: '215',
      quotedCost,
      totalCost
    };

    if (resinType === 'terapro220') {
      _totalEstimatedMaterialCost += totalCost;
    }

    setResults(prevResults => ({
      ...prevResults,
      terapro220ResinAggregate: _terapro220ResinAggregate
    }));

    // Pro Catalyst Powder for Terapro 220 resin aggregate
    unitQuantity =
      (_terapro220ResinAggregate.unitQuantity *
        getTemperatureUsageForResinAggregate(temperature)) /
      10;
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceCatalystPowder) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _terapro220ResinAggregateCatalystPowder = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '1 box of 10 * 0.1 kg bags',
      unitLabel: 'box',
      typicalCoverage: '10',
      quotedCost,
      totalCost
    };

    if (resinType === 'terapro220') {
      _totalEstimatedMaterialCost += totalCost;
    }

    setResults(prevResults => ({
      ...prevResults,
      terapro220ResinAggregateCatalystPowder:
        _terapro220ResinAggregateCatalystPowder
    }));

    // Terapro 250 resin aggregate
    unitQuantity = area / 57.5 + (waste / 100) * (area / 57.5);
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceResin) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _terapro250ResinAggregate = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '25 kg pails',
      unitLabel: 'pail',
      typicalCoverage: '57.5',
      quotedCost,
      totalCost
    };

    if (resinType === 'terapro250') {
      _totalEstimatedMaterialCost += totalCost;
    }

    setResults(prevResults => ({
      ...prevResults,
      terapro250ResinAggregate: _terapro250ResinAggregate
    }));

    // Pro Catalyst Powder for Terapro 250 resin aggregate
    unitQuantity =
      (_terapro250ResinAggregate.unitQuantity *
        getTemperatureUsageForResinAggregate(temperature)) /
      10;
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceCatalystPowder) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _terapro250ResinAggregateCatalystPowder = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '1 box of 10 * 0.1 kg bags',
      unitLabel: 'box',
      typicalCoverage: '10',
      quotedCost,
      totalCost
    };

    if (resinType === 'terapro250') {
      _totalEstimatedMaterialCost += totalCost;
    }

    setResults(prevResults => ({
      ...prevResults,
      terapro250ResinAggregateCatalystPowder:
        _terapro250ResinAggregateCatalystPowder
    }));

    // Pro Primer W Grid (only for Terapro 250 w/ pro grid)
    unitQuantity = area / 135 + (waste / 100) * (area / 135);
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceProPrimer) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _proPrimerWGrid = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '10 kg pails',
      unitLabel: 'pail',
      typicalCoverage: '135',
      quotedCost,
      totalCost
    };

    setResults(prevResults => ({
      ...prevResults,
      proPrimerWGrid: _proPrimerWGrid
    }));

    // Pro grid reinforcement (only for Terapro 250 w/ pro grid)
    unitQuantity = area / 1760 + (waste / 100) * (area / 1760);
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceResin) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _proGridReinforcement = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '1 roll',
      unitLabel: 'roll',
      typicalCoverage: '1760',
      quotedCost,
      totalCost
    };

    if (resinType === 'terapro250grid') {
      _totalEstimatedMaterialCost += totalCost;
    }

    setResults(prevResults => ({
      ...prevResults,
      proGridReinforcement: _proGridReinforcement
    }));

    // Pro catalyst liquid for Pro grid w (only for Terapro 250 w/ pro grid)
    unitQuantity =
      (_proPrimerWGrid.unitQuantity *
        getTemperatureUsageForProPrimer(temperature)) /
      10;
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceCatalystLiquid) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _proPrimerWGridCatalystLiquid = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '2.5 kg container',
      unitLabel: 'container',
      typicalCoverage: '10',
      quotedCost,
      totalCost
    };

    setResults(prevResults => ({
      ...prevResults,
      proPrimerWGridCatalystLiquid: _proPrimerWGridCatalystLiquid
    }));

    // Terapro 250 grid resin aggregate (only for Terapro 250 w/ pro grid)
    unitQuantity = area / 57.5 + (waste / 100) * (area / 57.5);
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceResin) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _terapro250GridResinAggregate = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '10 kg pails',
      unitLabel: 'pail',
      typicalCoverage: '57.5',
      quotedCost,
      totalCost
    };

    if (resinType === 'terapro250grid') {
      _totalEstimatedMaterialCost += totalCost;
    }

    setResults(prevResults => ({
      ...prevResults,
      terapro250GridResinAggregate: _terapro250GridResinAggregate
    }));

    // Pro catalyst powder for Terapro 250 grid resin aggregate (only for Terapro 250 w/ pro grid)
    unitQuantity =
      (_terapro250GridResinAggregate.unitQuantity *
        getTemperatureUsageForResinAggregate(temperature)) /
      10;
    unitQuantityRoundedUp = roundUpToOneDecimal(unitQuantity);
    unitQuantityRoundedUpInteger = roundUpToInteger(unitQuantity);
    quotedCost = includePrice ? Number(quotedPriceCatalystPowder) : 0;
    totalCost =
      Math.round(unitQuantityRoundedUpInteger * quotedCost * 100) / 100;
    const _terapro250GridResinAggregateCatalystPowder = {
      unitQuantity,
      unitQuantityRoundedUp,
      unitQuantityRoundedUpInteger,
      unitSize: '1 box of 10 * 0.1 kg bags',
      unitLabel: 'box',
      typicalCoverage: '10',
      quotedCost,
      totalCost
    };

    if (resinType === 'terapro250grid') {
      _totalEstimatedMaterialCost += totalCost;
    }

    const _proCatalystLiquidTotalUnitQuantityRoundedUp = roundUpToOneDecimal(
      _proCatalystLiquidTotalUnitQuantity
    );

    const _proCatalystLiquidTotalUnitQuantityRoundedUpInteger =
      roundUpToInteger(_proCatalystLiquidTotalUnitQuantity);

    const _proCatalystLiquidTotalCost =
      Math.round(
        _proCatalystLiquidTotalUnitQuantityRoundedUpInteger *
          Number(quotedPriceCatalystLiquid) *
          100
      ) / 100;

    const _proCatalystLiquidTotal = {
      unitQuantity: _proCatalystLiquidTotalUnitQuantity,
      unitQuantityRoundedUp: _proCatalystLiquidTotalUnitQuantityRoundedUp,
      unitQuantityRoundedUpInteger:
        _proCatalystLiquidTotalUnitQuantityRoundedUpInteger,
      unitSize: '2.5 kg container',
      unitLabel: 'container',
      typicalCoverage: '10',
      quotedCost: Number(quotedPriceCatalystLiquid),
      totalCost: _proCatalystLiquidTotalCost
    };

    setResults(prevResults => ({
      ...prevResults,
      terapro250GridResinAggregateCatalystPowder:
        _terapro250GridResinAggregateCatalystPowder,
      proCatalystLiquidTotal: _proCatalystLiquidTotal
    }));

    _totalEstimatedMaterialCost += _proCatalystLiquidTotalCost;

    setTotalEstimatedMaterialCost(_totalEstimatedMaterialCost);
  };

  useEffect(() => {
    setResinTypesSelectOptions();
  }, []);

  // useEffect to enable the calculate button, if all fields are filled
  useEffect(() => {
    if (validateForm()) {
      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
  }, [
    resinType,
    temperature,
    area,
    flashing,
    waste,
    includePrice,
    includeProColorFinish,
    quotedPriceProPrimer,
    quotedPriceResin,
    quotedPriceCatalystLiquid,
    quotedPriceCatalystPowder,
    quotedPriceProColorFinish,
    temperatureError
  ]);

  return {
    labels,
    optionsResinTypes,
    productNames,
    productCoverageLabels,
    resinType,
    temperature,
    formattedTemperature,
    temperatureError,
    area,
    formattedArea,
    areaError,
    flashing,
    formattedFlashing,
    flashingError,
    waste,
    wasteError,
    formattedWaste,
    includePrice,
    includeProColorFinish,
    quotedPriceProPrimer,
    formattedQuotedPriceProPrimer,
    quotedPriceResin,
    formattedQuotedPriceResin,
    quotedPriceCatalystLiquid,
    formattedQuotedPriceCatalystLiquid,
    quotedPriceCatalystPowder,
    formattedQuotedPriceCatalystPowder,
    quotedPriceProColorFinish,
    formattedQuotedPriceProColorFinish,
    disableButton,
    displayResults,
    results,
    totalEstimatedMaterialCost,
    flashingPrimerLayerDropdownValue,
    setFlashingPrimerLayerDropdownValue,
    flashingLayerDropdownValue,
    setFlashingLayerDropdownValue,
    colorFinishLayerDropdownValue,
    setColorFinishLayerDropdownValue,
    membranePrimerLayerDropdownValue,
    setMembranePrimerLayerDropdownValue,
    surfacingLayerDropdownValue220,
    setSurfacingLayerDropdownValue220,
    surfacingLayerDropdownValue250,
    setSurfacingLayerDropdownValue250,
    surfacingLayerDropdownValue250Grid,
    setSurfacingLayerDropdownValue250Grid,
    membranePrimerLayerDropdownValue250Grid,
    setMembranePrimerLayerDropdownValue250Grid,
    totalDropdownValue,
    setTotalDropdownValue,
    handleChangeResinType,
    handleChangeTemperature,
    handleBlurTemperature,
    handleChangeArea,
    handleBlurArea,
    handleChangeFlashing,
    handleBlurFlashing,
    handleChangeWaste,
    handleBlurWaste,
    handleClickCalculate,
    handleChangePriceYes,
    handleChangePriceNo,
    handleChangeProColorFinishYes,
    handleChangeProColorFinishNo,
    handleChangeQuotedPriceProPrimer,
    handleBlurQuotedPriceProPrimer,
    handleChangeQuotedPriceResin,
    handleBlurQuotedPriceResin,
    handleChangeQuotedPriceCatalystLiquid,
    handleBlurQuotedPriceCatalystLiquid,
    handleChangeQuotedPriceCatalystPowder,
    handleBlurQuotedPriceCatalystPowder,
    handleChangeQuotedPriceProColorFinish,
    handleBlurQuotedPriceProColorFinish
  };
}

export default useMaterialEstimatingCalculator;
